import React from 'react';
import { BarChart3, Layers, Repeat, ShieldCheck, Brain, Globe } from 'lucide-react';

const Feature = ({ icon, title, children }) => (
  <div className="bg-white h-64 rounded-xl shadow-md p-5 border border-gray-200">
    <div className="flex items-center mb-2">
      {icon}
      <h4 className="ml-2 text-base font-semibold text-gray-800">{title}</h4>
    </div>
    <div className="text-sm text-gray-600">{children}</div>
  </div>
);

const LandingPage = ({ onNavigate }) => {
  const heroBgUrl = `${process.env.PUBLIC_URL}/images/circular_economy_home.png`;
  return (
    <div className="w-screen min-h-[75vh] ">
        {/* Hero Section: responsive layout */}
        <section className="bg-cyan-600 w-screen min-h-[75vh] flex flex-col md:flex-row">
          {/* Top content for mobile, right for desktop */}
          <div className="order-1 md:order-2 flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-4">
              TriageOS — Revolutionizing Circular Asset Management
            </h1>
            <p className="text-white/90 text-base md:text-lg mb-8">
              AI-driven platform that helps manufacturers, OEMs, and asset managers optimize the lifecycle of high-value assets.
              Built for EU Digital Product Passport (DPP) compliance, Triage_OS delivers real-time traceability, predictive maintenance,
              and data-driven R-strategy recommendations to maximize value and reduce waste.
            </p>
            {/* CTA buttons for desktop */}
            <div className="hidden md:flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={() => onNavigate && onNavigate('management')}
                className="px-5 py-2.5 rounded-lg bg-gray-800 bg-white/ border border-gray-800 hover:bg-white/85 text-white hover:text-gray-900 font-medium shadow"
              >
                Get Started — Asset Manager
              </button>
          
              <button
                onClick={() => onNavigate && onNavigate('about')}
                className="px-5 py-2.5 rounded-lg bg-white/85 border border-gray-200 hover:bg-white text-gray-900 font-medium shadow"
              >
                About
              </button>
            </div>
            <div className="hidden md:block text-xs text-white/80">
              Backed by strong ESG alignment and EU regulatory tailwinds
            </div>
          </div>
          {/* Image in middle for mobile, left for desktop */}
          <div className="order-2 md:order-1 flex-1 flex items-center justify-center">
            <img
              src={heroBgUrl}
              alt="Circular Economy Home"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          {/* Bottom content for mobile */}
          <div className="order-3 md:hidden flex flex-col justify-center px-4 sm:px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <button
                onClick={() => onNavigate && onNavigate('management')}
                className="px-5 py-2.5 rounded-lg bg-white/95 border border-gray-200 hover:bg-white text-gray-900 font-medium shadow"
              >
                Get Started — Asset Manager
              </button>
              <button
                onClick={() => onNavigate && onNavigate('dashboard')}
                className="px-5 py-2.5 rounded-lg bg-white/95 border border-gray-200 hover:bg-white text-gray-900 font-medium shadow"
              >
                View Dashboard
              </button>
              <button
                onClick={() => onNavigate && onNavigate('about')}
                className="px-5 py-2.5 rounded-lg bg-white/95 border border-gray-200 hover:bg-white text-gray-900 font-medium shadow"
              >
                About
              </button>
            </div>
            <div className="text-xs text-white/80">
              Backed by strong ESG alignment and EU regulatory tailwinds
            </div>
          </div>
        </section>

      {/* Feature grid below hero */}
      <div className="container mx-auto max-w-5xl px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Feature icon={<ShieldCheck className="w-5 h-5 text-emerald-600" />} title="EU DPP-Ready">
            JSON-based Digital Product Passports capture identity, materials, lifecycle events, and performance data for compliance and traceability.
          </Feature>
          <Feature icon={<Brain className="w-5 h-5 text-indigo-600" />} title="AI-Powered Insights">
            Machine learning analyzes condition, market volatility, and materials to recommend optimal R-strategies (Reuse, Repair, Remanufacture, Recycle) and boost ROI.
          </Feature>
          <Feature icon={<Repeat className="w-5 h-5 text-cyan-600" />} title="Real-Time Monitoring">
            Predictive analytics and sensor data help prevent failures and extend asset life by 20–40%.
          </Feature>
          <Feature icon={<BarChart3 className="w-5 h-5 text-purple-600" />} title="Actionable KPIs">
            Track portfolio value, circular potential, MCI, DPP coverage, and EPR rates in a clear, role-based dashboard.
          </Feature>
          <Feature icon={<Layers className="w-5 h-5 text-amber-600" />} title="Open & Scalable">
            Built on Django + React with open-source foundations for rapid integration and lower cost of ownership.
          </Feature>
          <Feature icon={<Globe className="w-5 h-5 text-gray-700" />} title="Designed for SMEs">
            Affordable subscription pricing (€50–200/user/month) tailored for mid-sized manufacturers—no enterprise lock-in.
          </Feature>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
